import { Col, Divider, Row } from 'antd';
import { postApi } from 'apis';
import { PermissionContext } from 'components/Global/AuthLayout';
import Buttons from 'components/Global/Buttons';
import InputField from 'components/Global/InputField';
import {
	useCallback,
	useContext,
	useEffect,
	useReducer,
	useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'redux/store';
import {
	decrypt,
	hasFormError,
	invalidText,
	toastText,
	validateFormData,
} from 'utils/utils';
import { fetchCompanyWizard } from '../../../../../../redux/actions/payrollSettingWizardAction';
import styles from './index.module.scss';

const stanbicEncryptionKey = process.env.REACT_APP_STANBIC_ENCRYPTION_KEY ?? '';
const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY ?? '';

// Initial state for form data
const initialFormState = {
	stanbicDetails: {
		customerId: '',
		userId: '',
	},
	napsaDetails: {
		userName: '',
		password: '',
		clientId: '',
		employerAccountNumber: '',
	},
};

// Initial state for form errors
const initialErrorState = {
	stanbicDetails: {
		customerId: false,
		userId: false,
	},
	napsaDetails: {
		userName: false,
		password: false,
		clientId: false,
		employerAccountNumber: false,
	},
};

// Form reducer to centralize state updates
const formReducer = (state: any, action: any) => {
	switch (action.type) {
		case 'UPDATE_FIELD':
			return {
				...state,
				[action.section]: {
					...state[action.section],
					[action.field]: action.value,
				},
			};
		case 'UPDATE_SECTION':
			return {
				...state,
				[action.section]: {
					...state[action.section],
					...action.data,
				},
			};
		case 'SET_ALL':
			return { ...action.data };
		default:
			return state;
	}
};

// Error reducer
const errorReducer = (state: any, action: any) => {
	switch (action.type) {
		case 'SET_ERROR':
			return {
				...state,
				[action.section]: {
					...state[action.section],
					[action.field]: action.hasError,
				},
			};
		case 'SET_SECTION_ERRORS':
			return {
				...state,
				[action.section]: {
					...state[action.section],
					...action.errors,
				},
			};
		case 'CLEAR_SECTION_ERRORS':
			return {
				...state,
				[action.section]: Object.keys(state[action.section]).reduce(
					(acc, key) => ({ ...acc, [key]: false }),
					{}
				),
			};
		default:
			return state;
	}
};

// Props type definition
type Props = {
	onCancel: () => void;
	editData: any;
};

export const IntegrationDetails = (props: Props) => {
	const { onCancel, editData } = props;
	const context = useContext(PermissionContext);
	const dispatch = useDispatch<AppDispatch>();

	// State management with useReducer for better organization
	const [formData, dispatchFormData] = useReducer(
		formReducer,
		initialFormState
	);
	const [formErrors, dispatchFormErrors] = useReducer(
		errorReducer,
		initialErrorState
	);

	// UI state
	const [isStanbicLoading, setIsStanbicLoading] = useState(false);
	const [isNapsaLoading, setIsNapsaLoading] = useState(false);

	// Generic field change handler - centralized for all form sections
	const handleFieldChange = useCallback(
		(
			value: any,
			field: any,
			section: any,
			required = false,
			regex = null
		) => {
			// Update form data
			dispatchFormData({
				type: 'UPDATE_FIELD',
				section,
				field,
				value,
			});

			// Validate field if required
			if (required) {
				let hasError = false;
				if (typeof value === 'string') {
					hasError = invalidText(value);
				} else if (Array.isArray(value)) {
					hasError = value.length === 0;
				}

				// Check against regex if provided
				if (!hasError && typeof value === 'string' && regex) {
					const _regex = new RegExp(regex);
					hasError = !_regex.test(value);
				}

				// Update error state
				dispatchFormErrors({
					type: 'SET_ERROR',
					section,
					field,
					hasError,
				});
			}
		},
		[]
	);

	// Stanbic details submission handler
	const handleStanbicSubmit = useCallback(async () => {
		// Validate Stanbic form fields
		const stanbicDetailsErrors = validateFormData(
			formData.stanbicDetails,
			formErrors.stanbicDetails
		);

		// Update error states
		dispatchFormErrors({
			type: 'SET_SECTION_ERRORS',
			section: 'stanbicDetails',
			errors: stanbicDetailsErrors,
		});

		// Check if any errors exist
		const hasErrors = hasFormError(stanbicDetailsErrors);

		if (hasErrors) {
			return;
		}

		setIsStanbicLoading(true);

		try {
			// Prepare data for API
			const data = {
				stanbicDetails: formData.stanbicDetails,
			};

			await postApi('/company/stanbic-details', data);

			// Handle success
			toastText('Stanbic details updated successfully', 'success');
			// Clear errors on successful submission
			dispatchFormErrors({
				type: 'CLEAR_SECTION_ERRORS',
				section: 'stanbicDetails',
			});
		} catch (error: any) {
			// Handle error
			const message =
				error?.response?.data?.message ||
				'Something went wrong in updating Stanbic details';
			toastText(message, 'error');
		} finally {
			setIsStanbicLoading(false);
		}
	}, [formData.stanbicDetails, formErrors.stanbicDetails, dispatch]);

	// NAPSA details submission handler
	const handleNapsaSubmit = useCallback(async () => {
		// Validate NAPSA form fields
		const napsaDetailsErrors = validateFormData(
			formData.napsaDetails,
			formErrors.napsaDetails
		);

		// Update error states
		dispatchFormErrors({
			type: 'SET_SECTION_ERRORS',
			section: 'napsaDetails',
			errors: napsaDetailsErrors,
		});

		// Check if any errors exist
		const hasErrors = hasFormError(napsaDetailsErrors);

		if (hasErrors) {
			return;
		}

		setIsNapsaLoading(true);

		try {
			// Prepare data for API
			const data = {
				napsaUserDetails: formData.napsaDetails,
			};

			await postApi('/company/napsa-details', data);

			// Handle success
			toastText('NAPSA details updated successfully', 'success');
			dispatch(fetchCompanyWizard());

			// Clear errors on successful submission
			dispatchFormErrors({
				type: 'CLEAR_SECTION_ERRORS',
				section: 'napsaDetails',
			});
		} catch (error: any) {
			// Handle error
			const message =
				error?.response?.data?.message ||
				'Something went wrong in updating NAPSA details';
			toastText(message, 'error');
		} finally {
			setIsNapsaLoading(false);
		}
	}, [formData.napsaDetails, formErrors.napsaDetails, dispatch]);

	// Load existing data when available
	useEffect(() => {
		if (!editData) return;

		// Set Stanbic details if available
		if (editData?.StanbicDetails) {
				const decryptedCustomerId = decrypt(
					editData.StanbicDetails.stanbicCustomerId,
					stanbicEncryptionKey
				);
				const decryptedUserId = decrypt(
					editData.StanbicDetails.stanbicUserId,
					stanbicEncryptionKey
				);

				dispatchFormData({
					type: 'UPDATE_SECTION',
					section: 'stanbicDetails',
					data: {
						customerId: decryptedCustomerId,
						userId: decryptedUserId,
					},
				});
		}

		// Set NAPSA details if available
		if (editData?.NapsaDetails) {
				const decryptedUserName = decrypt(
					editData.NapsaDetails.userName || '',
					encryptionKey
				);
				const decryptedPassword = decrypt(
					editData.NapsaDetails.password || '',
					encryptionKey
				);
				const decryptedClientId = decrypt(
					editData.NapsaDetails.clientId || '',
					encryptionKey
				);

				dispatchFormData({
					type: 'UPDATE_SECTION',
					section: 'napsaDetails',
					data: {
						userName: decryptedUserName || '',
						password: decryptedPassword || '',
						clientId: decryptedClientId || '',
						employerAccountNumber:
							editData.NapsaDetails.employerAccountNumber || '',
					},
				});
		
		}
	}, [editData]);

	// Stanbic button configuration
	const stanbicButtons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'btn-cancel',
			isSubmit: false,
			disabled: isStanbicLoading,
			onclick: onCancel,
			fontSize: '1.6rem',
			minWidth: '9rem',
			minHeight: '3.5rem',
		},
		{
			text: 'Save',
			isLoading: isStanbicLoading,
			className: 'btn-blue',
			isSubmit: false,
			disabled: false,
			onclick: handleStanbicSubmit,
			fontSize: '1.6rem',
			minWidth: '9rem',
			minHeight: '3.5rem',
		},
	];

	// NAPSA button configuration
	const napsaButtons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'btn-cancel',
			isSubmit: false,
			disabled: isNapsaLoading,
			onclick: onCancel,
			fontSize: '1.6rem',
			minWidth: '9rem',
			minHeight: '3.5rem',
		},
		{
			text: 'Save',
			isLoading: isNapsaLoading,
			className: 'btn-blue',
			isSubmit: false,
			disabled: false,
			onclick: handleNapsaSubmit,
			fontSize: '1.6rem',
			minWidth: '9rem',
			minHeight: '3.5rem',
		},
	];

	// Check if user is admin
	if (!context.isAdmin) {
		return (
			<div style={{ textAlign: 'center', padding: '2rem' }}>
				<p style={{ fontSize: '1.6rem', color: '#ff4d4f' }}>
					Access Denied: Admin privileges required to view integration
					details.
				</p>
			</div>
		);
	}

	return (
			<div style={{ padding: '10px 0' }}>
				{/* Stanbic Details Section */}
				<div style={{ marginBottom: '20px' }}>
					<div
						style={{
							fontSize: '1.6rem',
							fontWeight: '600',
							color: '#584495',
							marginBottom: '15px',
						}}
					>
						Stanbic Bank Integration
					</div>
					<Row gutter={16}>
						<Col span={12}>
							<InputField
								name="Customer Id"
								value={formData.stanbicDetails.customerId}
								label="Customer Id"
								required={true}
								helperText="Enter Valid customer Id"
								onChange={(value) =>
									handleFieldChange(
										value,
										'customerId',
										'stanbicDetails',
										true
									)
								}
								isError={formErrors.stanbicDetails.customerId}
							/>
						</Col>
						<Col span={12}>
							<InputField
								name="User Id"
								value={formData.stanbicDetails.userId}
								label="User Id"
								required={true}
								helperText="Enter Valid user Id"
								onChange={(value) =>
									handleFieldChange(
										value,
										'userId',
										'stanbicDetails',
										true
									)
								}
								isError={formErrors.stanbicDetails.userId}
							/>
						</Col>
					</Row>
					<div style={{ marginTop: '15px', textAlign: 'left' }}>
						<Buttons buttons={stanbicButtons} />
					</div>
				</div>

				<Divider style={{ margin: '15px 0', borderColor: '#b6b4bd' }} />

				{/* NAPSA Details Section */}
				<div>
					<div
						style={{
							fontSize: '1.6rem',
							fontWeight: '600',
							color: '#584495',
							marginBottom: '15px',
						}}
					>
						NAPSA Integration
					</div>
					<Row gutter={16}>
						<Col span={12}>
							<InputField
								name="User Name"
								value={formData.napsaDetails.userName}
								label="User Name"
								required={true}
								helperText="Enter Valid user name"
								onChange={(value) =>
									handleFieldChange(
										value,
										'userName',
										'napsaDetails',
										true
									)
								}
								isError={formErrors.napsaDetails.userName}
							/>
						</Col>
						<Col span={12}>
							<InputField
								name="Password"
								value={formData.napsaDetails.password}
								label="Password"
								required={true}
								helperText="Enter Valid password"
								onChange={(value) =>
									handleFieldChange(
										value,
										'password',
										'napsaDetails',
										true
									)
								}
								isError={formErrors.napsaDetails.password}
								type="password"
							/>
						</Col>
						<Col
							span={12}
							className={`${styles['col']} margin-top-10`}
						>
							<InputField
								name="Client Id"
								value={formData.napsaDetails.clientId}
								label="Client Id"
								required={true}
								helperText="Enter Valid client Id"
								onChange={(value) =>
									handleFieldChange(
										value,
										'clientId',
										'napsaDetails',
										true
									)
								}
								isError={formErrors.napsaDetails.clientId}
							/>
						</Col>
						<Col
							span={12}
							className={`${styles['col']} margin-top-10`}
						>
							<InputField
								name="ICare account number"
								value={
									formData.napsaDetails.employerAccountNumber
								}
								label="ICare account number"
								required={true}
								helperText="Enter Valid ICare account number"
								onChange={(value) =>
									handleFieldChange(
										value,
										'employerAccountNumber',
										'napsaDetails',
										true
									)
								}
								isError={
									formErrors.napsaDetails
										.employerAccountNumber
								}
							/>
						</Col>
					</Row>
					<div style={{ marginTop: '15px', textAlign: 'left' }}>
						<Buttons buttons={napsaButtons} />
					</div>
				</div>
			</div>
	);
};
